/**
 * useGarmentPrintLookup - Garment lookup for print station
 *
 * Same pattern as catcher's useGarmentLookup — imperative fetch via trpc utils.
 */
import { useState, useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import type { PrintFileLanes } from '@threadheads/shared';

export interface PrintGarmentLookupResult {
  garmentUnit: {
    id: string;
    sku: string;
    shopifySku: string | null;
    productName: string;
    variantName: string | null;
    size: string | null;
    color: string | null;
    status: string;
    itemIndex: number;
    totalInOrder: number;
    isRedo: boolean;
    redoReason: string | null;
    imageUrl: string | null;
    /** Short-TTL signed URL for the re-hosted custom artwork preview, or null (falls back to imageUrl). Mirrors the backend PrintGarmentInfo.customPreviewUrl. */
    customPreviewUrl: string | null;
    /** Host-vetted raw customizer preview URL — lazy browser fallback before the re-host cron fills customPreviewUrl. Mirrors backend PrintGarmentInfo.customPreviewSourceUrl. */
    customPreviewSourceUrl: string | null;
    /**
     * Display previews. Mirrors backend `PrintGarmentInfo`, and the field
     * names are the shared `ProductionPreviewFacts` contract in
     * `@/components/production/ProductionPreviewTile`, so a garment unit can
     * be handed straight to `buildFinishedGarmentPreviewEntries`.
     *
     * The card renders GARMENT IMAGES ONLY: the `*MockupPreviewUrl` pair, the
     * custom's combined preview, or the catalogue photo. The bare artwork
     * fields below still arrive and are deliberately not rendered.
     *
     * None of these is a printable source. The print path resolves its file
     * from the storage probe + `print-source-resolver.ts`; nothing here
     * reaches the bridge, the manifest or the print agent.
     */
    isCustomProduct: boolean;
    /** Front garment mockup (standards only). Display only. */
    frontMockupPreviewUrl: string | null;
    /** Back garment mockup. Never the front mockup, never a print source. */
    backMockupPreviewUrl: string | null;
    /** Bare front print artwork: variant artwork for a standard, the supplied
     *  Method C file for a custom. Supplied, not rendered on this station. */
    frontPreviewUrl: string | null;
    /** Bare back print artwork. Same standing as the front. */
    backPreviewUrl: string | null;
    artworkBarcode: string | null;
    printMethod: string | null;
    requiresPretreat: boolean;
    frontPrintedAt: string | null;
    backPrintedAt: string | null;
  };
  order: {
    id: string;
    orderName: string;
    customerName: string | null;
    totalPodItems: number;
    totalPapItems: number;
  };
  /**
   * Semantic contract: see backend type at
   * apps/backend/src/modules/print-station/print-station.types.ts → `PrintGarmentLookupResult`
   * for the authoritative documentation. This client-side declaration mirrors the
   * server shape; keep both JSDoc blocks aligned when semantics change.
   *
   * AUTHORITATIVE for PW customs (HEAD-checked at scan time via Promise.allSettled on the storage adapter).
   * For standard non-CUST products: DERIVED from the `^2[A-Z]` SKU regex
   *   - non-dual SKU (`TEE-...`)  → hasFront=true,  hasBack=false
   *   - dual SKU    (`2TEE-...`) → hasFront=true,  hasBack=true
   * Frontend branches on hasFront / hasBack for indicator + button rendering.
   * Mutation routing (markDualSidePrinted vs markPrinted) uses isDualPrint.
   * Invariant: isDualPrint === hasFront && hasBack.
   *
   * If a future product category cannot guarantee file presence by SKU rule,
   * introduce a `fileVerified: boolean` discriminator alongside these fields
   * rather than overloading the meaning here.
   */
  hasFront: boolean;
  hasBack: boolean;
  isDualPrint: boolean;
  alreadyPrinted: boolean;
  /**
   * BLU-1336: per-side arx4 / png file evidence for the station's printer
   * file-type guard. See the backend `PrintGarmentLookupResult.printFileLanes`
   * for the invariants (null for standards and anything unproven; `absent`
   * only from that scan's own live probe; never feeds hasFront / hasBack).
   */
  printFileLanes: PrintFileLanes | null;
}

export function useGarmentPrintLookup() {
  const [data, setData] = useState<PrintGarmentLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { selectedLocation } = useLocation();

  const utils = trpc.useUtils();

  const lookup = useCallback(async (qrCode: string) => {
    if (!selectedLocation?.id) {
      setError('No location selected');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await utils.printStation.getGarmentForPrint.fetch({
        qrCode,
        locationId: selectedLocation.id,
      });
      setData(result as PrintGarmentLookupResult);
      setError(null);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Failed to look up garment');
    } finally {
      setIsLoading(false);
    }
  }, [utils.printStation.getGarmentForPrint, selectedLocation?.id]);

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    data,
    error,
    isLoading,
    lookup,
    clear,
  };
}
